import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import AdsComponent from './AdsComponent';
import './AlgorithmHardware.css';

const AlgorithmHardware = () => {
  const { algo } = useParams();
  const [data, setData] = useState([]);
  const [selectedAlgo, setSelectedAlgo] = useState(algo || '');
  const [selectedHardware, setSelectedHardware] = useState('');
  const [filteredAlgorithms, setFilteredAlgorithms] = useState([]);
  const [filteredHardware, setFilteredHardware] = useState([]);
  const [cryptocurrencies, setCryptocurrencies] = useState([]);
  const [showCryptos, setShowCryptos] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get('/api/currencies/algorithms-hardware');
        setData(response.data);
      } catch (err) {
        console.error('Error fetching data', err);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedAlgo) {
      const algoData = data.find(item => item.algo === selectedAlgo);
      if (algoData) {
        setFilteredHardware(algoData.hardware_type);
        fetchCryptocurrencies(selectedAlgo);
      }
    }
  }, [selectedAlgo, data]);

  useEffect(() => {
    if (selectedHardware) {
      const hardwareData = data.filter(item => item.hardware_type.includes(selectedHardware));
      const algorithms = hardwareData.map(item => item.algo);
      setFilteredAlgorithms(algorithms);

      if (algorithms.length > 0) {
        fetchCryptocurrenciesByAlgorithms(algorithms);
      }
    }
  }, [selectedHardware, data]);

  const fetchCryptocurrencies = async (algorithm) => {
    try {
      const response = await axios.get(`/api/currencies/algorithm?algo=${algorithm}`);
      const uniqueCryptos = removeDuplicateCryptos(response.data);
      setCryptocurrencies(uniqueCryptos);
      setShowCryptos(true);
    } catch (err) {
      console.error('Error fetching cryptocurrencies', err);
    }
  };

  const fetchCryptocurrenciesByAlgorithms = async (algorithms) => {
    const allCryptos = [];
    for (const algo of algorithms) {
      try {
        const response = await axios.get(`/api/currencies/algorithm?algo=${algo}`);
        allCryptos.push(...response.data);
      } catch (err) {
        console.error(`Error fetching cryptocurrencies for ${algo}`, err);
      }
    }
    const uniqueCryptos = removeDuplicateCryptos(allCryptos);
    setCryptocurrencies(uniqueCryptos);
    setShowCryptos(true);
  };

  const removeDuplicateCryptos = (cryptos) => {
    const uniqueCryptos = Array.from(new Set(cryptos.map(crypto => crypto.symbol)))
      .map(symbol => cryptos.find(crypto => crypto.symbol === symbol));
    return uniqueCryptos;
  };

  const handleAlgoChange = (e) => {
    setSelectedAlgo(e.target.value);
    setSelectedHardware('');
    setFilteredHardware([]);
    setCryptocurrencies([]);
    setShowCryptos(false);
  };

  const handleHardwareChange = (e) => {
    setSelectedHardware(e.target.value);
    setSelectedAlgo('');
    setFilteredAlgorithms([]);
    setCryptocurrencies([]);
    setShowCryptos(false);
  };

  return (
    <div className="AlgorithmHardwareSelector-container">
      <h2>Algorithm and Hardware Selector</h2>
      <p className="description">
        This tool helps miners, developers, and cryptocurrency enthusiasts find the best hardware for mining specific algorithms or the algorithms compatible with their hardware. 
        By selecting an algorithm, you can view the types of hardware that are supported. Alternatively, by selecting a hardware type (e.g., CPU, GPU, ASIC), you'll see the algorithms that can be mined using that hardware and the cryptocurrencies that can be mined.
      </p>

      <h2>Algorithm Miner Statistics</h2>
      <p className="description">
        Explore detailed mining statistics for various algorithms. This section provides insights into the total miners, unique coins, total pools, and average 24-hour BTC revenue for each algorithm.
      </p>
      <a href="/algorithmList" className="link">
        View Algorithm Miner Statistics
      </a>

      <div className="sidebar-ad">
          <AdsComponent requiredSize="728x90" />
      </div>    

      <div className="selector-grid">
        <div className="algorithm-selector">
          <label htmlFor="algo-select">Select Algorithm:</label> 
          <select id="algo-select" value={selectedAlgo} onChange={handleAlgoChange}>
            <option value="">-- Select an Algorithm --</option>
            {data.map((item, index) => (
              <option key={index} value={item.algo}>{item.algo}</option>
            ))}
          </select>

          {selectedAlgo && (
            <div className="hardware-result">
              <h3>Hardware for Algorithm: {selectedAlgo}</h3>
              <ul>
                {filteredHardware.map((hardware, index) => (
                  <li key={index}>{hardware}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="hardware-selector">
          <label htmlFor="hardware-select">Select Hardware Type:</label>
          <select id="hardware-select" value={selectedHardware} onChange={handleHardwareChange}>
            <option value="">-- Select Hardware Type --</option>
            {['CPU', 'GPU', 'ASIC', 'FPGA', 'Unknown'].map((hardware, index) => (
              <option key={index} value={hardware}>{hardware}</option>
            ))}
          </select>

          {selectedHardware && (
            <div className="algorithm-result">
              <h3>Algorithms for Hardware Type: {selectedHardware}</h3>
              <ul>
                {filteredAlgorithms.map((algo, index) => (
                  <li key={index}>{algo}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {showCryptos && cryptocurrencies.length > 0 && (
        <div className="cryptocurrency-result">
          <h3>Cryptocurrencies You Can Mine</h3>
          <table className="Cryptocurrency-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Name</th>
                <th>Algorithm</th>
              </tr>
            </thead>
            <tbody>
              {cryptocurrencies.map((crypto, index) => (
                <tr key={index}>
                  <td>{crypto.symbol}</td>
                  <td>{crypto.name}</td>
                  <td>{crypto.algo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AlgorithmHardware;
